import React from 'react';
import {
  Building2, CalendarDays, Banknote, ClipboardList,
  Scale, Settings, GraduationCap
} from 'lucide-react';

const ConfigDashboard = ({ configData, onNavigateToTab }) => {
  // Calculate statistics for each category
  const getStats = () => {
    const stats = [
      {
        category: 'basic',
        title: 'Basic Configuration',
        Icon: Building2,
        items: [
          { name: 'Departments', count: configData.departments?.length || 0, color: 'blue' },
          { name: 'Locations', count: configData.locations?.length || 0, color: 'green' },
          { name: 'Classifications', count: configData.classifications?.length || 0, color: 'purple' },
          { name: 'Designations', count: configData.designations?.length || 0, color: 'yellow' },
          { name: 'Employment Types', count: configData.employmentTypes?.length || 0, color: 'pink' },
          { name: 'Pay Grades', count: configData.payGrades?.length || 0, color: 'indigo' }
        ]
      },
      {
        category: 'qualifications',
        title: 'Qualifications',
        Icon: GraduationCap,
        items: [
          { name: 'Institution Types', count: configData.institutionTypes?.length || 0, color: 'blue' },
          { name: 'Qualification Levels', count: configData.qualificationLevels?.length || 0, color: 'green' },
          { name: 'Fields of Study', count: configData.fieldsOfStudy?.length || 0, color: 'purple' },
          { name: 'Certification Authorities', count: configData.certificationAuthorities?.length || 0, color: 'yellow' },
          { name: 'Certification Categories', count: configData.certificationCategories?.length || 0, color: 'pink' },
          { name: 'Certification Templates', count: configData.certificationTemplates?.length || 0, color: 'indigo' }
        ]
      },
      {
        category: 'leave',
        title: 'Leave & Holidays',
        Icon: CalendarDays,
        items: [
          { name: 'Leave Types', count: configData.leaveTypes?.length || 0, color: 'blue' },
          { name: 'Leave Policies', count: configData.leavePolicies?.length || 0, color: 'green' },
          { name: 'Public Holidays', count: configData.publicHolidays?.length || 0, color: 'yellow' }
        ]
      },
      {
        category: 'payroll',
        title: 'Payroll & Benefits',
        Icon: Banknote,
        items: [
          { name: 'Salary Structures', count: configData.salaryStructures?.length || 0, color: 'blue' },
          { name: 'CPF Rates', count: configData.cpfRates?.length || 0, color: 'green' }
        ]
      },
      {
        category: 'policies',
        title: 'Work Policies',
        Icon: ClipboardList,
        items: [
          { name: 'Workweek Policies', count: configData.workweekPolicies?.length || 0, color: 'blue' },
          { name: 'Overtime Policies', count: configData.overtimePolicies?.length || 0, color: 'green' },
          { name: 'Notice Policies', count: configData.noticePolicies?.length || 0, color: 'purple' },
          { name: 'Retirement Policies', count: configData.retirementPolicies?.length || 0, color: 'yellow' }
        ]
      },
      {
        category: 'compliance',
        title: 'Compliance',
        Icon: Scale,
        items: [
          { name: 'Foreign Worker Levies', count: configData.foreignWorkerLevies?.length || 0, color: 'blue' },
          { name: 'WICA Compensations', count: configData.wicaCompensations?.length || 0, color: 'green' }
        ]
      },
      {
        category: 'system',
        title: 'System Administration',
        Icon: Settings,
        items: [
          { name: 'System Settings', count: configData.systemSettings?.length || 0, color: 'blue' },
          { name: 'Integration Keys', count: configData.integrationKeys?.length || 0, color: 'green' },
          { name: 'Custom Fields', count: configData.customFields?.length || 0, color: 'purple' },
          { name: 'Audit Configs', count: configData.auditConfigs?.length || 0, color: 'yellow' }
        ]
      },
    ];
    
    return stats;
  };

  const getColorClasses = (color) => {
    const colorMap = {
      blue: 'bg-blue-100 text-blue-800',
      green: 'bg-green-100 text-green-800',
      purple: 'bg-purple-100 text-purple-800',
      yellow: 'bg-yellow-100 text-yellow-800',
      pink: 'bg-pink-100 text-pink-800',
      indigo: 'bg-indigo-100 text-indigo-800'
    };
    return colorMap[color] || 'bg-gray-100 text-gray-800';
  };

  const getTotalConfigurations = () => {
    return Object.values(configData).reduce((total, items) => {
      return total + (Array.isArray(items) ? items.length : 0);
    }, 0);
  };

  const getUnconfiguredCategories = () => {
    const stats = getStats();
    return stats.filter(stat => 
      stat.items.every(item => item.count === 0)
    );
  };

  const stats = getStats();
  const totalConfigurations = getTotalConfigurations();
  const unconfiguredCategories = getUnconfiguredCategories();

  return (
    <div className="space-y-6">
      {/* Overall Statistics — 2-up on mobile (never single-column), 3 across
          from lg. Values are small integers so the icon stays beside them. */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{stats.length}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Configuration Categories</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{totalConfigurations}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Total Configurations</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 15.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="min-w-0">
              <p className="text-xl sm:text-2xl font-bold text-gray-900">{unconfiguredCategories.length}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Needs Attention</p>
            </div>
          </div>
        </div>
      </div>

      {/* Configuration Categories */}
      <div className="space-y-6">
        {stats.map((stat) => (
          <div key={stat.category} className="bg-white rounded-2xl shadow border">
            <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-9 h-9 bg-red-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <stat.Icon className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate">{stat.title}</h3>
                </div>
                <button
                  onClick={() => onNavigateToTab(stat.category)}
                  className="btn btn-admin-secondary flex-shrink-0"
                  title={`Configure ${stat.title}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="hidden sm:inline">Configure</span>
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {stat.items.map((item) => (
                  <div key={item.name} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                    <span className="text-sm font-medium text-gray-700">{item.name}</span>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getColorClasses(item.color)}`}>
                      {item.count}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recommendations */}
      {unconfiguredCategories.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">Recommendations</h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>The following categories need configuration to ensure proper HR system operation:</p>
                <ul className="mt-2 list-disc list-inside">
                  {unconfiguredCategories.map(category => (
                    <li key={category.category}>
                      <button
                        onClick={() => onNavigateToTab(category.category)}
                        className="text-red-600 underline hover:text-red-700 transition-colors font-medium"
                      >
                        {category.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConfigDashboard; 